import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ArrowRight, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const searchData = [
  { text: "Dashboard", section: "Espace", path: "/dashboard", keywords: "accueil synthèse" },
  { text: "Forum", section: "Collaboration", path: "/forum", keywords: "dossiers échanges" },
  { text: "Leads", section: "Relations", path: "/prospects/leads", keywords: "contacts" },
  {
    text: "Prospects",
    section: "Relations",
    path: "/prospects/prospects",
    keywords: "clients contacts",
  },
  {
    text: "Familles",
    section: "Relations",
    path: "/prospects/familles",
    keywords: "patrimoine clients",
  },
  { text: "Partenaires", section: "Relations", path: "/partenaires", keywords: "réseau" },
  { text: "Produits", section: "Gestion", path: "/produits", keywords: "finance placements" },
  {
    text: "Transactions",
    section: "Gestion",
    path: "/transactions",
    keywords: "opérations",
  },
  { text: "Calendrier", section: "Organisation", path: "/calendrier", keywords: "agenda dates" },
  { text: "Fichiers", section: "Documents", path: "/fichiers", keywords: "ftp stockage" },
  {
    text: "Documentation",
    section: "Documents",
    path: "/documents",
    keywords: "fichiers ressources",
  },
  { text: "Support", section: "Aide", path: "/support", keywords: "assistance contact" },
];

function normalize(value) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

export default function Browser() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listboxId = useId();

  const searchResults = useMemo(() => {
    const query = normalize(searchTerm.trim());
    if (!query) return [];

    return searchData.filter(({ text, section, keywords }) =>
      normalize(`${text} ${section} ${keywords}`).includes(query),
    );
  }, [searchTerm]);

  const showResults = searchTerm.trim().length > 0 && (isFocused || isMobileOpen);
  const highlightedIndex = Math.min(activeIndex, Math.max(searchResults.length - 1, 0));

  useEffect(() => {
    setActiveIndex(0);
  }, [searchTerm]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsFocused(false);
        setIsMobileOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (isMobileOpen) {
      inputRef.current?.focus();
    }
  }, [isMobileOpen]);

  useEffect(() => {
    const handleShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();

        if (window.matchMedia("(max-width: 639px)").matches) {
          setIsMobileOpen(true);
        } else {
          inputRef.current?.focus();
        }
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const closeSearch = () => {
    setSearchTerm("");
    setIsFocused(false);
    setIsMobileOpen(false);
  };

  const handleSearchSelect = (path) => {
    closeSearch();
    navigate(path);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      closeSearch();
      return;
    }

    if (!searchResults.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % searchResults.length);
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + searchResults.length) % searchResults.length);
    }

    if (event.key === "Enter") {
      event.preventDefault();
      handleSearchSelect(searchResults[highlightedIndex].path);
    }
  };

  return (
    <div ref={rootRef} className="relative z-50">
      <button
        type="button"
        onClick={() => setIsMobileOpen(true)}
        aria-label="Ouvrir la recherche"
        aria-expanded={isMobileOpen}
        className="workspace-icon-button sm:hidden"
      >
        <Search aria-hidden="true" className="size-5" />
      </button>

      {isMobileOpen && (
        <button
          type="button"
          aria-label="Fermer la recherche"
          onClick={closeSearch}
          className="fixed inset-0 z-50 bg-overlay backdrop-blur-sm sm:hidden"
        />
      )}

      <div
        className={`${
          isMobileOpen ? "fixed inset-x-4 top-4 z-[60]" : "hidden"
        } sm:relative sm:inset-auto sm:z-auto sm:block sm:w-56 md:w-64 lg:w-80 xl:w-96`}
      >
        <div className="workspace-search-control">
          <Search aria-hidden="true" className="size-5 shrink-0 text-subtle" />
          <input
            ref={inputRef}
            type="search"
            role="combobox"
            aria-label="Rechercher une page"
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-expanded={showResults}
            aria-activedescendant={
              showResults && searchResults.length
                ? `${listboxId}-${highlightedIndex}`
                : undefined
            }
            autoComplete="off"
            placeholder="Rechercher dans l’espace…"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            onFocus={() => setIsFocused(true)}
            onKeyDown={handleKeyDown}
            className="workspace-search-input"
          />

          {searchTerm ? (
            <button
              type="button"
              onClick={() => {
                setSearchTerm("");
                inputRef.current?.focus();
              }}
              aria-label="Effacer la recherche"
              className="workspace-search-clear"
            >
              <X aria-hidden="true" className="size-4" />
            </button>
          ) : (
            <span className="workspace-search-shortcut hidden lg:inline-flex" aria-hidden="true">
              ⌘ K
            </span>
          )}
        </div>

        {showResults && (
          <div className="workspace-card-elevated absolute left-0 right-0 top-full mt-2 overflow-hidden">
            <div className="border-b border-workspace px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-subtle">
                {searchResults.length
                  ? `${searchResults.length} résultat${searchResults.length > 1 ? "s" : ""}`
                  : "Aucun résultat"}
              </p>
            </div>

            {searchResults.length > 0 ? (
              <ul
                id={listboxId}
                role="listbox"
                className="max-h-[min(24rem,calc(100dvh-7rem))] overflow-y-auto p-2"
              >
                {searchResults.map((item, index) => (
                  <li
                    id={`${listboxId}-${index}`}
                    key={item.path}
                    role="option"
                    aria-selected={highlightedIndex === index}
                  >
                    <button
                      type="button"
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => handleSearchSelect(item.path)}
                      className="workspace-search-option group"
                    >
                      <span className="min-w-0 text-left">
                        <span className="block truncate text-sm font-semibold text-primary">
                          {item.text}
                        </span>
                        <span className="block truncate text-xs text-subtle">{item.section}</span>
                      </span>
                      <ArrowRight
                        aria-hidden="true"
                        className="size-4 shrink-0 text-subtle transition-transform group-hover:translate-x-0.5"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-5 py-8 text-center">
                <Search aria-hidden="true" className="mx-auto size-6 text-subtle" />
                <p className="mt-2 text-sm text-muted">Essayez avec un autre mot-clé.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
